import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import {
  DASHBOARD_OPERATORS_TO_QUERY,
  DATA_TYPE,
  FIELD_WITHOUT_DATA_ID,
  SPECIAL_FIELDS,
  TIME_UNITS_LIST
} from "./constants/DashboardTemplatesConstants";
import { FilterField, FilterOfCollection } from "./models/FilterOfCollection";
import { SectionOfDashboard } from "./models/SectionOfDashboard";
import { CollectionFilter } from "./models/CollectionFilter";
import { FilterItemOfQuery } from "./models/FilterItemOfQuery";
import { Http } from "@status/codes";
import { Query } from "./models/Query";
import { cloneDeep } from "lodash";

function fixPriceLevelValues(
  filter: FilterOfCollection,
  indexCollection: number,
  sectionsOfDashboardStore: SectionOfDashboard[],
  indexSectionOfDashboard: number
) {
  const filters =
    sectionsOfDashboardStore[indexSectionOfDashboard].collections[
      indexCollection
    ].filters;

  const cupsField = filters.find(
    filter => (filter.field?.[0] as FilterField)?.id === "cups"
  );

  const clientsNames = (Array.isArray(filter.value) ? filter.value : []).map(
    (value: DynamicSelectData) => value.name
  );

  if (!cupsField?.value?.length) {
    return (Array.isArray(filter.value) ? filter.value : []).map(
      (value: DynamicSelectData | string) =>
        (value as DynamicSelectData)?.id || (value as string)
    );
  }

  let clientsIds: string[] = [];
  (cupsField as FilterOfCollection).value.forEach(
    (value: DynamicSelectData) => {
      const clients = Object.entries(value.fields)
        .filter(item => {
          return clientsNames.includes(
            (item[1] as { name: string; price: string }).name
          );
        })
        .map((item: [string, unknown]) => item[0]);

      clientsIds = Array.from(
        new Set<string>([...clientsIds, ...clients])
      );
    }
  );

  return clientsIds;
}

const checkIfRangeOperator = (operator?: DynamicSelectData[]) => {
  return operator?.[0]?.id === DASHBOARD_OPERATORS_TO_QUERY.RANGE;
};

export const fixOperator = (operator?: DynamicSelectData[]) => {
  if (checkIfRangeOperator(operator)) {
    return DASHBOARD_OPERATORS_TO_QUERY.BETWEEN;
  }
  return operator?.[0]?.id;
};

function getStartDateAtZero(type: string) {
  const now = new Date();
  const result = new Date(now);

  // Funciones reutilizables
  const setHourStart = (date: Date) => {
    date.setMinutes(0, 0, 0);
  };

  const setDayStart = (date: Date) => date.setHours(0, 0, 0, 0);

  const setWeekStart = (date: Date) => {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    date.setDate(date.getDate() + diff);
    date.setHours(0, 0, 0, 0);
  };

  const setMonthStart = (date: Date) => {
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
  };

  const setYearStart = (date: Date) => {
    date.setMonth(0);
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
  };

  const strategies: Record<string, (date: Date) => void> = {
    [TIME_UNITS_LIST.LAST_HOURS]: setHourStart,
    [TIME_UNITS_LIST.NEXT_HOURS]: setHourStart,
    [TIME_UNITS_LIST.LAST_DAYS]: setDayStart,
    [TIME_UNITS_LIST.NEXT_DAYS]: setDayStart,
    [TIME_UNITS_LIST.LAST_WEEKS]: setWeekStart,
    [TIME_UNITS_LIST.NEXT_WEEKS]: setWeekStart,
    [TIME_UNITS_LIST.LAST_MONTHS]: setMonthStart,
    [TIME_UNITS_LIST.NEXT_MONTHS]: setMonthStart,
    [TIME_UNITS_LIST.LAST_YEARS]: setYearStart,
    [TIME_UNITS_LIST.NEXT_YEARS]: setYearStart
  };

  const strategy = strategies[type];
  if (strategy) {
    strategy(result);
  }
  return result;
}

function subtractFromDate(
  unit: string,
  amount: number,
  baseDate: Date
): Date | number | undefined {
  if (amount < 0) {
    return;
  }

  if (!(baseDate instanceof Date)) {
    return;
  }

  const result = new Date(baseDate);

  const strategies: Record<
    string,
    (date: Date, amt: number) => Date | number
  > = {
    [TIME_UNITS_LIST.LAST_HOURS]: (date, amt) => {
      date.setHours(date.getHours() - amt);
      return date.getTime();
    },
    [TIME_UNITS_LIST.LAST_DAYS]: (date, amt) => {
      date.setDate(date.getDate() - amt);
      return date;
    },
    [TIME_UNITS_LIST.LAST_WEEKS]: (date, amt) => {
      date.setDate(date.getDate() - amt * 7);
      return date;
    },
    [TIME_UNITS_LIST.LAST_MONTHS]: (date, amt) => {
      date.setMonth(date.getMonth() - amt);
      return date;
    },
    [TIME_UNITS_LIST.LAST_YEARS]: (date, amt) => {
      date.setFullYear(date.getFullYear() - amt);
      return date;
    },
    [TIME_UNITS_LIST.NEXT_HOURS]: (date, amt) => {
      date.setHours(date.getHours() + amt + 1);
      return date.getTime();
    },
    [TIME_UNITS_LIST.NEXT_DAYS]: (date, amt) => {
      date.setDate(date.getDate() + amt);
      return date;
    },
    [TIME_UNITS_LIST.NEXT_WEEKS]: (date, amt) => {
      date.setDate(date.getDate() + (amt + 1) * 7 - 1);
      return date;
    },
    [TIME_UNITS_LIST.NEXT_MONTHS]: (date, amt) => {
      date.setMonth(date.getMonth() + amt + 1);
      date.setDate(0);
      return date;
    },
    [TIME_UNITS_LIST.NEXT_YEARS]: (date, amt) => {
      date.setFullYear(date.getFullYear() + amt + 1);
      date.setMonth(0);
      date.setDate(0);
      return date;
    }
  };

  const strategy = strategies[unit];
  return strategy ? strategy(result, amount) : result;
}

export function rangeValuesDates(value: string[]) {
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear());
    return `${year}-${month}-${day}`;
  };

  const dateToCalculate = getStartDateAtZero(value[1]);
  const startDate = subtractFromDate(
    value[1],
    Number(value[0]) - 1,
    dateToCalculate
  );

  const formattedDateOne =
    value[1] === TIME_UNITS_LIST.LAST_HOURS ||
    value[1] === TIME_UNITS_LIST.NEXT_HOURS
      ? startDate
      : formatDate(startDate as Date);

  const formattedDateTwo =
    value[1] === TIME_UNITS_LIST.LAST_HOURS ||
    value[1] === TIME_UNITS_LIST.NEXT_HOURS
      ? Date.now()
      : new Date().toISOString().slice(0, 10);

  if (value[1].includes("next")) {
    return [formattedDateTwo, formattedDateOne];
  }

  return [formattedDateOne, formattedDateTwo];
}

export function fixValues(
  filter: FilterOfCollection,
  indexCollection: number,
  sectionsOfDashboardStore: SectionOfDashboard[],
  indexSectionOfDashboard: number
) {
  const field = (filter.field?.[0] as FilterField) || undefined;
  const value = Array.isArray(filter.value) ? filter.value : [];

  if (!field) {
    return [];
  }

  if (field.id == "priceLevel") {
    return fixPriceLevelValues(
      filter,
      indexCollection,
      sectionsOfDashboardStore,
      indexSectionOfDashboard
    ) as string[];
  }

  if (checkIfRangeOperator(filter.operator as DynamicSelectData[])) {
    return rangeValuesDates(value as string[]);
  }

  if (field.type == DATA_TYPE.TIME_UNIT) {
    if (value.length > 1) {
      return [`${value[0]}-${value[1]} ${field.description || "year"}`];
    }

    return [`${value} ${field.description || "year"}`];
  }

  if (["text", "number"].includes(field.type)) {
    if (value.length === 2) {
      return [value[0], value[1]];
    }
    return [(value as unknown) as string];
  }
  return value.map((item: DynamicSelectData | string) => {
    if ((item as DynamicSelectData)?.id == FIELD_WITHOUT_DATA_ID) {
      return "";
    }
    return (item as DynamicSelectData).id || (item as string);
  });
}

export function buildFilters(
  collections: CollectionFilter[],
  filters: FilterItemOfQuery[],
  indexCollectionFilter: number,
  sectionsOfDashboardStore: SectionOfDashboard[],
  indexSectionOfDashboard: number,
  editMode: boolean
) {
  for (const [indexCollection, collection] of collections.entries()) {
    if (editMode && indexCollection > indexCollectionFilter) {
      return filters;
    }
    if (collection.filters.length === 0) {
      filters.push({
        view: collection.collection[0].id,
        field: "",
        operator: "",
        value: []
      });
      continue;
    }

    for (const filter of collection.filters) {
      if (!filter?.field?.[0]) return filters;
      filters.push({
        view: collection?.collection?.[0]?.id || collection?.id || "",
        field: (filter.field[0] as FilterField).id,
        operator: fixOperator(filter.operator as DynamicSelectData[]) || "",
        value: fixValues(
          filter,
          indexCollection,
          sectionsOfDashboardStore,
          indexSectionOfDashboard
        )
      });
    }
  }

  return filters;
}

export function fixQuery(
  querySaved: Query,
  allFiltersOfSection: FilterItemOfQuery[]
) {
  const query = cloneDeep(querySaved as Query);
  query.filters = query.filters.map(
    (filter: FilterItemOfQuery, index: number) => {
      const originalFilter = allFiltersOfSection[index];
      if (
        ((originalFilter?.operator[0] as unknown) as DynamicSelectData)?.id ==
        DASHBOARD_OPERATORS_TO_QUERY.RANGE
      ) {
        return {
          ...filter,
          operator: DASHBOARD_OPERATORS_TO_QUERY.BETWEEN,
          value: rangeValuesDates(originalFilter.value as string[]) as string[]
        };
      }
      return filter;
    }
  );

  return query;
}

function fixGroupBy(
  groupBy: (DynamicSelectData | string)[],
  collection: CollectionFilter,
  dashboard = false,
  filters: FilterItemOfQuery[]
) {
  if (
    !Array.isArray(groupBy) ||
    (!collection?.collection?.length && !dashboard)
  )
    return [];

  const SPECIAL_FIELDS_COLLECTION = SPECIAL_FIELDS.filter(field =>
    field.toLocaleLowerCase().includes(collection.id as string)
  );
  const specialFields = filters.filter(item =>
    SPECIAL_FIELDS_COLLECTION.includes(item.field)
  );

  if (specialFields.length > 0) {
    return groupBy
      .filter(
        (item: string | DynamicSelectData) =>
          !SPECIAL_FIELDS_COLLECTION.includes((item as FilterField).id)
      )
      .map(item => ({
        view: dashboard ? collection.id : collection.collection[0].id,
        field: typeof item === "string" ? item : item.id
      }));
  }

  return groupBy.map(item => ({
    view: dashboard ? collection.id : collection.collection[0].id,
    field: typeof item === "string" ? item : item.id
  }));
}

function fixSeeBy(seeBy: DynamicSelectData[] | DynamicSelectData) {
  if (Array.isArray(seeBy) && seeBy.length > 0) {
    return seeBy[0].id;
  }
  return (seeBy as DynamicSelectData)?.id || "";
}

export function buildAndExecuteQuery(
  collection: CollectionFilter,
  filters: FilterItemOfQuery[],
  indexCollection: number,
  indexCollectionFilter: number,
  groupBy: DynamicSelectData[],
  rangeSize: number | null,
  seeBy: DynamicSelectData[],
  dashboard?: boolean
) {
  const query = {
    selectFields: {
      fields: [],
      agregations: []
    },
    seeBy: fixSeeBy(
      (collection.seeBy as DynamicSelectData[]) ||
        (seeBy as DynamicSelectData[])
    ),
    rangeSize: rangeSize || "", //collection.rangeSize || "",
    principalView: collection?.collection?.[0]?.id || collection?.id || "",
    filters: filters,
    groupFields: [{}]
  };

  if (indexCollection == indexCollectionFilter) {
    query.groupFields = fixGroupBy(
      groupBy as DynamicSelectData[],
      collection,
      dashboard || false,
      filters
    );
    if (
      !(groupBy as any[]).some(groupBy => groupBy.type == DATA_TYPE.TIME_STAMP)
    ) {
      query.seeBy = "";
    } else {
      if (seeBy?.length > 0) {
        query.seeBy = fixSeeBy(seeBy);
      }
    }
    query.rangeSize = rangeSize ?? "";
  } else {
    query.groupFields = fixGroupBy(
      (collection.groupBy as DynamicSelectData[]) ||
        (groupBy as DynamicSelectData[]),
      collection,
      dashboard || false,
      filters
    );
  }

  if (!query.groupFields) {
    return null;
  }

  if (query.groupFields.length == 0) {
    return null;
  }

  return query;
}

export function prepareData(
  chartName: string,
  xAxis: [],
  data: [],
  groups: {},
  indexCollection: number,
  groupBy: DynamicSelectData[],
  rangeSize: number | null,
  seeBy: DynamicSelectData[],
  indexSectionOfDashboard: number,
  chartColor: { hex: "" },
  indexCollectionFilter: number,
  idQuery?: string
) {
  const chartData = {
    chartName: chartName,
    xAxis,
    data,
    groups
  };
  const prepareData = {
    indexSectionOfDashboard: indexSectionOfDashboard,
    indexCollectionFilter: indexCollection,
    groupBy: groupBy,
    seeBy: seeBy,
    rangeSize: rangeSize,
    chartName: "",
    chartData,
    chartColor: "",
    notFound: false,
    idQuery: idQuery
  };
  if (indexCollectionFilter == indexCollection) {
    prepareData.chartName = chartName;
    prepareData.chartColor = chartColor.hex;
  }

  return prepareData;
}

export function detectChartBarClick(
  e: MouseEvent,
  chartRef: any,
  xAxisData: any[],
  seriesData: any[],
  seriesName: string
): { index: number; label: any; value: any; seriesName: string } | null {
  if (!chartRef || !chartRef.chart) {
    return null;
  }

  const chart = chartRef.chart;

  // Obtener el canvas
  const canvas = chart.getDom().querySelector("canvas");
  if (!canvas) {
    return null;
  }

  // Calcular coordenadas relativas al canvas
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  // Verificar si está en el área del gráfico
  const isInGrid = chart.containPixel("grid", [x, y]);

  if (!isInGrid) {
    return null;
  }

  // Convertir pixel a coordenada del gráfico
  const gridCoord = chart.convertFromPixel("grid", [x, y]);

  if (!gridCoord || gridCoord.length === 0) {
    return null;
  }

  const dataIndex = Math.round(gridCoord[0]);

  // Validar que el índice esté dentro del rango
  if (dataIndex < 0 || dataIndex >= xAxisData.length) {
    return null;
  }

  // Retornar los datos de la barra clickeada
  return {
    index: dataIndex,
    label: xAxisData[dataIndex],
    value: seriesData[dataIndex],
    seriesName
  };
}

/**
 * Detecta si un click derecho en un chart tipo Pie/Doughnut de ECharts está sobre un segmento
 * y retorna los datos correspondientes
 *
 * @param e - MouseEvent del click derecho
 * @param chartRef - Referencia al componente v-chart
 * @param seriesData - Array con los datos de la serie (formato: [{name, value}])
 * @param seriesName - Nombre de la serie
 * @returns Objeto con los datos del segmento clickeado o null si no se detectó
 */
export function detectChartPieClick(
  e: MouseEvent,
  chartRef: any,
  seriesData: Array<{ name: any; value: any }>,
  seriesName: string
): { index: number; label: any; value: any; seriesName: string } | null {
  if (!chartRef || !chartRef.chart) {
    return null;
  }

  const chart = chartRef.chart;

  // Obtener el canvas
  const canvas = chart.getDom().querySelector("canvas");
  if (!canvas) {
    return null;
  }

  // Calcular coordenadas relativas al canvas
  const rect = canvas.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  // Para gráficos tipo pie, necesitamos verificar si está en el área de la serie
  // ECharts indexa las series como "series.0", "series.1", etc.
  const params = chart.containPixel({ seriesIndex: 0 }, [x, y]);

  if (!params) {
    return null;
  }

  // Usar getZr() para obtener información del elemento bajo el cursor
  const zr = chart.getZr();
  if (!zr) {
    return null;
  }

  // Buscar elemento bajo las coordenadas
  let dataIndex = -1;

  // Iterar sobre los datos de la serie para encontrar cuál está bajo el cursor
  for (let i = 0; i < seriesData.length; i++) {
    const isInPiece = chart.containPixel({ seriesIndex: 0, dataIndex: i }, [
      x,
      y
    ]);

    if (isInPiece) {
      dataIndex = i;
      break;
    }
  }

  // Validar que se encontró un segmento válido
  if (dataIndex < 0 || dataIndex >= seriesData.length) {
    return null;
  }

  // Retornar los datos del segmento clickeado
  return {
    index: dataIndex,
    label: seriesData[dataIndex].name,
    value: seriesData[dataIndex].value,
    seriesName
  };
}

/**
 * Busca un usuario por documento y retorna la información necesaria para navegar
 * @param userDocument - Documento del usuario a buscar
 * @param userService - Servicio de usuarios
 * @returns Objeto con userId si se encontró, null si no
 */
export async function getUserByDocument(
  userDocument: string,
  userService: any
): Promise<string | null> {
  try {
    const userResponse = await userService.getUserByFilters({
      userDocument
    });

    if (
      userResponse.code === Http.Found &&
      userResponse.data?.userArray?.[0]?.id
    ) {
      return userResponse.data.userArray[0].id;
    }

    return null;
  } catch (error) {
    return null;
  }
}
